import { OutputParams } from 'query-string';
import { Options } from 'rrule';
import { Segment } from '~src/interfaces/segment.interface';

import { FiltersRespI } from '../../interfaces/filters.interface';
import { PaginationI } from '../../interfaces/pagination.interface';
import { ContentLocalesI } from '../../interfaces/session.types';
import { OkrActivityI } from '../../interfaces/okrActivity.interface';
import { SortingRespI } from '../../interfaces/sorting.interface';
import { VisibilityOptions } from '../../modules/okrs/constants/options';

export enum ObjectiveStatus {
  DRAFT = 'draft',
  PLANNED = 'planned',
  IN_PROGRESS = 'in_progress',
  OVERDUE = 'overdue',
  CLOSED = 'closed',
}

export interface WeightedItem {
  weighting: number | null;
  weighting_locked: boolean;
}

export interface ObjectivesResp {
  pagination: PaginationI;
  filters: FiltersRespI;
  sorting: null | SortingRespI;
  items: ObjectiveItem[];
}

export interface ObjectiveItemUpdate {
  id: string;
  name: ContentLocalesI;
  description: ContentLocalesI;
  start_date: string;
  due_date: string;
  visibility: string;
  visibilities?: string[];
  vis_owners_managers: boolean;
  reminder_options: Options;
  status: ObjectiveStatus;
  progress: number;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
  owners: string[];
  labels?: string[];
}

export type SubObjectiveItem = ObjectiveItem & WeightedItem;

export interface ObjectiveItem {
  activities?: OkrActivityI[];
  id: string;
  name: ContentLocalesI;
  description: ContentLocalesI;
  start_date: string;
  due_date: string;
  visibility: VisibilityOptions;
  visibilities?: Segment[];
  vis_owners_managers: boolean;
  reminder_options: Options;
  real_status: ObjectiveStatus;
  status: ObjectiveStatus;
  progress: number;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
  owners?: OwnerI[];
  creator_id: string;
  creator: OwnerI;
  key_result_owners?: OwnerI[];
  labels?: ObjectiveLabelI[];
  has_sub_objectives: boolean;
  sub_objectives?: SubObjectiveItem[] /*depending on `?include=sub_objectives` */;
}

export interface ObjectiveLabelI {
  id: string;
  name: string;
}

export interface OwnerI {
  uuid: string;
  firstname: string;
  lastname: string;
  email: string;
  imageUrl: string | null;
  userPermissions?: any;
}

export enum SSFilterTypes {
  Name = 'filter.name',
  DueDateFrom = 'filter.due_date_from',
  DueDateTo = 'filter.due_date_to',
  ProgressFrom = 'filter.progress_from',
  ProgressTo = 'filter.progress_to',
}

export enum MSFilterTypes {
  HasRelations = 'filter.has_relations',
  Owner = 'filter.owner',
  Status = 'filter.status',
  Label = 'filter.label',
}

export enum ObjectiveFilterTypes {
  Stream = 'filter.stream',
}

export enum RelationTypes {
  SubObjectives = 'sub_objectives',
}

export interface ObjectivesQueryI {
  page?: number;
  sort?: string;
  [SSFilterTypes.Name]?: string;
  [MSFilterTypes.HasRelations]?: RelationTypes[];
  [MSFilterTypes.Owner]?: string[];
  [SSFilterTypes.DueDateFrom]?: string;
  [SSFilterTypes.DueDateTo]?: string;
  [MSFilterTypes.Status]?: ObjectiveStatus[];
  [SSFilterTypes.ProgressFrom]?: number;
  [SSFilterTypes.ProgressTo]?: number;
  [MSFilterTypes.Label]?: string[];
  [ObjectiveFilterTypes.Stream]?: string;
}

function asArray<T extends string>(val: string | string[] | undefined): T[] {
  if (typeof val === 'string') {
    return [val as T];
  }
  if (Array.isArray(val)) {
    return val as T[];
  }

  return [];
}

function asNumber(val: string | string[] | undefined): number | undefined {
  if (typeof val === 'string') {
    const numVal = parseFloat(val);
    if (!isNaN(numVal)) {
      return numVal;
    }
  }
  return;
}

export function queryParamsToObjectivesQuery(parsedParams: OutputParams): ObjectivesQueryI {
  const objectivesQuery: ObjectivesQueryI = parsedParams as ObjectivesQueryI;

  // map everything that's not string
  objectivesQuery[MSFilterTypes.HasRelations] = asArray(parsedParams[MSFilterTypes.HasRelations]);
  objectivesQuery[MSFilterTypes.Label] = asArray(parsedParams[MSFilterTypes.Label]);
  objectivesQuery[MSFilterTypes.Owner] = asArray(parsedParams[MSFilterTypes.Owner]);
  objectivesQuery[MSFilterTypes.Status] = asArray(parsedParams[MSFilterTypes.Status]);
  objectivesQuery.page = asNumber(parsedParams.page);
  objectivesQuery[SSFilterTypes.ProgressFrom] = asNumber(parsedParams[SSFilterTypes.ProgressFrom]);
  objectivesQuery[SSFilterTypes.ProgressTo] = asNumber(parsedParams[SSFilterTypes.ProgressTo]);

  return objectivesQuery;
}

export interface OkrLabel {
  id: string;
  name: string;
}

export interface OkrLabelAdd {
  name: string;
}

export interface PostSubobjectiveBody {
  id: string;
  weighting: number | null;
}

export interface PostObjectiveApiBody {
  name: ContentLocalesI;
  description: ContentLocalesI;
  start_date: string;
  due_date: string;
  visibility: string;
  owners: string[];
  labels: string[];
  visibilities: string[];
  vis_owners_managers: boolean;
  status: 'draft' | 'active';
  reminder_options: any;
  key_results: PostKeyResultBody[];
  subobjectives: PostSubobjectiveBody[];
}

export interface PostKeyResultBody {
  name: ContentLocalesI;
  min: number;
  max: number;
  owners: string[];
  weighting: number | null;
}
